import React, { useMemo, useState, useEffect } from "react";
import useAsyncRetry from "react-use/lib/useAsyncRetry";
import { InputCustomChangeEventHandler } from "../../types";
import { get } from "../../utils/helpers";
import { loadCountryRegionsFromPlaces, getPlaceById } from "../../utils/places";
import useT from "../../hooks/useT";
import InputSelectCustom, {
  ReactSelectOption,
  InputSelectCustomProps
} from "../InputSelectCustom";

// @TODO when Omit is available as a built-in TS helper:
// interface InputCountryProps extends Omit<InputSelectCustomProps, "options" | "onChange"> {}
export interface InputCountryRegionProps
  extends Pick<
    InputSelectCustomProps,
    Exclude<keyof InputSelectCustomProps, "options" | "onChange">
  > {
  /**
   * If the input is optional, you can show a "prefer not to say" option which will
   * set the value to an empty string.
   *
   * @default false
   */
  showEmptyOption?: boolean;
  /** A country to load the regions from. If this is empty then the field will be disabled. */
  country?: string | number | null;
  /** Event handler to publish changes to the controlling parent component. */
  onChange: InputCustomChangeEventHandler;
}

export default function InputCountryRegion({
  name,
  value,
  searchable = true,
  clearable = true,
  country,
  showEmptyOption = false,
  disabled = false,
  lang,
  onChange,
  ...props
}: InputCountryRegionProps) {
  const { t, i18n } = useT("regions");
  const lng = lang || i18n.language;

  const regions = useAsyncRetry(() => loadCountryRegionsFromPlaces());

  const [regionRetry, setRegionRetry] = useState(0);
  useEffect(() => {
    if (regions.error && regionRetry < 3) {
      regions.retry();
      setRegionRetry(regionRetry + 1);
    }
  }, [regions.error, regionRetry]);

  const options = useMemo(() => {
    const _options: ReactSelectOption[] = [];

    if (!country) {
      return _options;
    }

    if (showEmptyOption) {
      _options.push({
        label: t("general.fields.notSet.label", {
          lng
        }),
        value: null
      });
    }

    if (regions.value && regions.value[country]) {
      regions.value[country].forEach(p => {
        const regionId = get(p, "identifier");
        //const regionName = get(p, "name");
        const regionName = t(regionId, { lng });
        _options.push({
          label: regionName,
          value: regionId
        });
      });
    }

    return _options;
  }, [country, regions.value, lng, clearable]);

  // Reset region value when the country is changed
  useEffect(() => {
    getPlaceById(value).then(place => {
      if (get(place, "geo.addressCountry") !== country) {
        onChange(null, name);
      }
    });
  }, [country, value, name]);

  return (
    <InputSelectCustom
      {...props}
      name={name}
      value={value}
      options={options}
      disabled={
        disabled ||
        !country ||
        regions.loading ||
        Boolean(regions.error) ||
        !options.length
      }
      clearable={clearable}
      searchable={searchable}
      lang={lng}
      onChange={onChange}
    />
  );
}
